<template>
  <div>
    <ValidationObserver v-slot="{ invalid }" ref="formValidationObserver" class="page">
      <!--Header-->
      <div class="page-header flex-shrink-0 mb-3 bg-white border-bottom" v-if="!DetailsOpen">
        <div class="container-fluid px-md-4 px-3">
          <div class="row no-gutters justify-content-between align-items-center">
            <h3 class="mb-0 col-md-auto col order-md-1 order-1">
              {{ pageName }}
            </h3>
            <div class="col-md-auto col-12 d-flex justify-content-md-center justify-content-start order-md-2 order-3 mt-md-0 mt-2">
              <b-button-group class="btn-group-filter w-100" v-if="!isBlueRidgeOrMadisonCommunities">
                <b-button class="btn-light px-4" :class="{ 'active': (currentView === 1) }"
                  @click="$router.replace('/procedures/1')">Incidents</b-button>
                <b-button class="btn-light px-4" :class="{ 'active': (currentView === 2) }"
                  @click="$router.replace('/procedures/2')">Tasks <span class="d-md-inline d-none">/
                    Inspections</span></b-button>
              </b-button-group>
            </div>
            <div class="col-auto order-md-3 order-2">
              <b-btn variant="success" @click="showNewProcedureModal">
                <i class="fas fa-plus"></i> Add <span class="d-md-inline d-none">{{ areaName }}</span>
              </b-btn>
            </div>
          </div>
        </div>
      </div>

      <!--Search-->
      <div class="container-fluid px-md-4 px-3 py-3 bg-white border-bottom mb-3" v-show="!DetailsOpen">
        <div class="row no-gutters d-flex align-items-center">
          <div class="col-lg-4 col-md-6">
            <b-input-group>
              <b-form-input v-model="filter" type="search" id="filterInput" placeholder="Type to Search"></b-form-input>
            </b-input-group>
          </div>
          <b-button :disabled="!filter" variant="link" @click="filter = ''">Clear</b-button>
        </div>
      </div>

      <!--Table-->
      <div v-show="!DetailsOpen & currentView===1" class="container-fluid flex-grow-1 overflow-auto px-md-4 px-3">
        <div v-show="procedures">
          <b-table id="procedures-table" bordered class="mb-5 rg-table" :items="procedureProvider"
            :fields="procedureTableData.fields" no-local-filtering :filter="filter" no-local-sorting
            :sortDesc.sync="sortDesc" :busy.sync="isBusy">
            <template v-slot:cell(type)="data">
              <div v-if="data.item.eventType" class="emphasis">
                {{ data.item.eventType.name }}
              </div>
            </template>
            <template v-slot:cell(category)="data">
              <div class="emphasis" v-if="data.item.eventCategory">
                {{ data.item.eventCategory.name }}
              </div>
            </template>
            <template v-slot:cell(visibleTo)="data">
              <b-tooltip v-show="data.item.visibleToPositionTypes.length" :target="'visibleTo_'+data.item.id"
                non-interactive no-fade>{{formatPositionTypes(data.item.visibleToPositionTypes)}}</b-tooltip>
              <div :id="'visibleTo_'+data.item.id" class="truncate">
                {{formatPositionTypes(data.item.visibleToPositionTypes)}}
              </div>
            </template>
            <template v-slot:cell(id)="data">
              <b-btn title="Edit Procedure" @click="showEditProcedureModal(data.item.id)" variant="link">
                <i class="fas fa-edit"></i>
              </b-btn>
              <b-btn title="Copy Procedure"
                @click="showCopyProcedureModal(data.item.id, data.item.eventType.name, (data.item.eventCategory ?  data.item.eventCategory.name : null), data.item.eventType.id, (data.item.eventCategory ?  data.item.eventCategory.id : null))"
                variant="link">
                <i class="fas fa-copy"></i>
              </b-btn>
            </template>
          </b-table>
        </div>
      </div>
      <div v-show="!DetailsOpen & currentView===2" class="container-fluid flex-grow-1 overflow-auto px-md-4 px-3">
        <!--Table-->
        <div v-show="routineTaskProcedures">
          <b-table id="tasks-table" bordered class="mb-5 rg-table" :items="taskProvider" :fields="taskTableData.fields"
            no-local-filtering :filter="filter" no-local-sorting :sortDesc.sync="sortDesc" :busy.sync="isBusy">
            <template v-slot:cell(name)="data">
              <div class="emphasis">
                {{ data.item.name }}
              </div>
            </template>
            <template v-slot:cell(id)="data">
              <b-btn title="Edit Routine Task" @click="showEditProcedureModal(data.item.id)" variant="link">
                <i class="fas fa-edit"></i>
              </b-btn>
            </template>
          </b-table>
        </div>
      </div>

      <!--Add Procedure-->
      <div v-show="DetailsOpen" class="procedure-edit-scroll">
        <b-form>
          <!--Header Add-->
          <div class="page-header procedure-edit-header">
            <div class="container d-flex justify-content-between align-items-center">
              <h3 v-show="mode==='details'">{{areaName}} Steps</h3>
              <div class="d-flex justify-content-end">
                <b-button type="button" v-if="mode!=='preview'" class="btn btn-warning mr-1" @click="mode='preview'">
                  <span class="d-md-inline d-none">Preview</span>
                  <i class="fas fa-search"></i>
                </b-button>
                <b-button type="button" v-if="mode!=='preview'" variant="primary" class="btn mr-1"
                  :disabled="invalid || (isNewFlag && isDuplicateFlag) || flowIssues.length > 0 || serverIssues.length > 0"
                  @click="doSaveProcedure">Save</b-button>
                <b-button type="button" v-if="mode!=='preview'" class="btn btn-light mr-1"
                  @click="toggleModal">Close</b-button>
              </div>
            </div>
          </div>

          <b-alert :show="serverIssues.length > 0" variant="danger" role="alert" dismissible
            class="mx-3 mt-2" @dismissed="serverIssues = []">
            <h5 class="alert-heading">The server rejected this save</h5>
            <ul class="mb-0 pl-3">
              <li v-for="(issue, idx) in serverIssues" :key="'server-issue-' + idx">
                {{ issue.stepLabel }}: {{ issue.message }}
              </li>
            </ul>
          </b-alert>

          <!--Preview-->
          <div v-show="mode==='preview'">
            <div class="alert alert-warning m-0 d-flex justify-content-between align-items-center mb-4">
              <h5 class="mb-0">
                <strong>Preview Mode</strong>
              </h5>
              <b-button variant="warning" v-if="mode!=='details'" @click="mode='details'">
                Exit Preview Mode
                <b-icon-x />
              </b-button>
            </div>
            <div class="container">
              <div class="row">
                <div class="col-12">
                  <procedureNavigator :questions="allCurrentQuestions" :showIndex="procedureNavigatorIndex"
                    class="mb-3 preview-questions" />
                </div>
              </div>
              <div class="row">
                <div class="col-12 d-flex justify-content-between">
                  <b-button variant="secondary" @click="procedureNavigatorIndex--">
                    <b-icon-chevron-left />Prev
                  </b-button>
                  <b-button variant="secondary" @click="procedureNavigatorIndex++">
                    Next
                    <b-icon-chevron-right />
                  </b-button>
                </div>
              </div>
            </div>
          </div>

          <div class="row flex-nowrap procedure-flow-row" v-if="mode==='details'">
          <div class="col procedure-main-col">
          <div class="w-100 container form-container p-0">
            <!--Details-->
            <div v-if="mode==='details'" class="row my-4 px-3">
              <div class="col-12 d-flex justify-content-between align-items-center">
                <label class="section-label">
                  <i class="fas fa-info-circle"></i> {{areaName}} Details
                </label>
                <div>
                  <b-dropdown size="sm" right id="dropdown-procedure-help" text="Help">
                    <div class="p-3" style="min-width: 320px">
                      <p>First define the circumstances for to begin.</p>
                      <p>
                        <strong>
                          <i>For example:</i>
                        </strong>
                        <i>As a company I need to create a procedure for a floods occuring within structures.</i> Based
                        on this select
                        <strong>Water/Flood for</strong>
                        <i>Response Type and</i>
                        <strong>Structure</strong> for the
                        <i>Category</i>.
                      </p>
                    </div>
                  </b-dropdown>
                </div>
              </div>
              <div class="col-12" v-if="currentView===1">
                <b-form-group>
                  <b-alert :show="isDuplicateFlag && isNewFlag" variant="warning">A procedure with this type and
                    category already exists.</b-alert>
                </b-form-group>
                <div class="row">
                  <div class="col-lg-6 col-12">
                     <b-form-group>
                      <label class="control-label">Response Type</label>
                      <ValidationProvider name="Response Type" rules="required" v-slot="validation">
                        <b-form-select :disabled="!isNewFlag" :options="eventTypeOptions" v-model="selectedType"
                          :state="spr2_getValidationState(validation)" aria-describedby="response-type-feedback">
                          <template #first>
                            <b-form-select-option :value="null" disabled>(Required) The type for this
                              procedure.</b-form-select-option>
                          </template>
                        </b-form-select>
                        <b-form-invalid-feedback id="response-type-feedback">{{ validation.errors[0]
                          }}</b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-form-group>
                  </div>
                  <div class="col-lg-6 col-12">
                    <b-form-group>
                      <label class="control-label">Category</label>
                      <b-form-select :disabled="!isNewFlag" :options="eventCategoryOptions" v-model="selectedCategory">
                        <template #first>
                          <b-form-select-option :value="null" disabled>(Optional) Select this if you want the procedure to
                            be specific to a category.</b-form-select-option>
                        </template>
                      </b-form-select>
                    </b-form-group>
                  </div>
                </div>
                <b-form-group>
                  <b-tooltip target="visibleTo" non-interactive no-fade>Select or Unselect multiple roles by pressing
                    control key [ctrl].</b-tooltip>
                  <div style="cursor: pointer" id="visibleToLabel" @click="toggleVisibleToExpanded" class="btn btn-light">
                    <label style="cursor: pointer" >Configure Role Visibility <span>({{
                        visibleToRolesCount }})</span></label>
                    <span><i :class="visibleToRolesExpanded ? 'fas fa-caret-up': 'fas fa-caret-down'"></i></span>
                    <i id="visibleTo" class="fas fa-question-circle ml-1"></i>
                  </div>
                  <b-form-select v-if="procedureForEdit && visibleToRolesExpanded" multiple :select-size="8"
                    v-model="procedureForEdit.visibleToPositionTypes" :options="allPositionTypesListOptions">
                  </b-form-select>
                </b-form-group>
              </div>
              <div class="col-12" v-if="currentView===2">
                <b-form-group>
                  <label class="control-label">Name</label>
                  <ValidationProvider name="Name" rules="required" v-slot="validation">
                    <b-form-input v-model="selectedName" :state="spr2_getValidationState(validation)">
                    </b-form-input>
                  </ValidationProvider>
                </b-form-group>
              </div>
            </div>
            <!--Questions-->
            <div v-if="mode==='details'" class="bg-light px-4">
              <label class="section-label mt-4 mb-2">
                <i class="fas fa-tasks"></i> {{areaName}} Steps
              </label>
              <span class="category-chip issue-count-chip ml-2" :class="flowIssues.length ? (hasBlockingFlowIssues ? 'category-chip-danger' : 'category-chip-warning') : 'category-chip-success'">
                {{ flowIssues.length ? (flowIssues.length + (flowIssues.length === 1 ? ' issue' : ' issues')) : 'All clear' }}
              </span>
              <div class="procedure-items pb-5">
                <div class="card-add my-1 rounded" @click="addNewQuestion(0)"><i class="fas fa-plus"></i></div>
                <div v-for="(questionForEdit, index) in allCurrentQuestions" v-bind:key="questionForEdit.id"
                  track-by="id" class="card-question" :id="'step-card-' + questionForEdit.id">
                  <div class="d-flex flex-row" :class="{ 'newQuestion': isNewQuestionFlag(questionForEdit.id) }">
                    <!--Card Numbering-->
                    <div class="px-2 d-flex align-items-center justify-content-center card-number mr-md-4 mr-2 bg-secondary text-white">
                      <span class="d-flex justify-content-center">{{index+1}}</span>
                    </div>
                    <!--Card Type Selection-->
                    <div class="flex-grow-1 card shadow-sm"
                      :class="[stepCardStateClass(questionForEdit.id), { 'step-pulse': pulseStepId === questionForEdit.id }]">
                      <div class="px-3">
                        <div class="row mt-3 mb-2">
                          <div class="col d-flex align-items-center justify-content-start">
                            <div class="form-group m-0">
                              <b-form-radio-group v-model="questionForEdit.typeId" class="d-flex">
                                <label
                                  class="btn btn-light d-flex flex-column justify-content-center mr-1 mb-0 switch-btn">
                                  <b-form-radio value="5d537d8a0537a484cda16b96">
                                    <span>
                                      <i class="fas fa-check-circle"></i>
                                      <strong>Yes/No</strong>
                                    </span>
                                  </b-form-radio>
                                </label>
                                <label
                                  class="btn btn-light d-flex flex-column justify-content-center mr-1 mb-0 switch-btn">
                                  <b-form-radio value="5d539144c7541f800a262427">
                                    <span>
                                      <i class="fas fa-font"></i>
                                      <strong>Text
                                        <span class="d-md-inline d-none px-0">Input</span>
                                      </strong>
                                    </span>
                                  </b-form-radio>
                                </label>
                                <label
                                  class="btn btn-light d-flex flex-column justify-content-center mr-1 mb-0 switch-btn">
                                  <b-form-radio value="5ddc90bb46d3a6fab5a2731d">
                                    <span>
                                      <i class="fas fa-thumbs-up"></i>
                                      <strong>Acknowledge</strong>
                                    </span>
                                  </b-form-radio>
                                </label>
                                <label class="btn btn-light d-flex flex-column justify-content-center mb-0 switch-btn">
                                  <b-form-radio value="5f1e1542699b5fbdf8176d3e"
                                    @change="checklistClicked(questionForEdit)">
                                    <span>
                                      <i class="fas fa-tasks"></i>
                                      <strong>Checklist</strong>
                                    </span>
                                  </b-form-radio>
                                </label>
                                <label
                                  class="btn btn-light d-flex flex-column justify-content-center mr-1 mb-0 switch-btn">
                                  <b-form-radio value="64f08edb6403dc3b99d3bcec">
                                    <span>
                                      <i class="fas fa-upload"></i>
                                      <strong>Image Upload</strong>
                                    </span>
                                  </b-form-radio>
                                </label>
                              </b-form-radio-group>
                            </div>
                          </div>
                          <div class="col">
                            <div class="d-flex justify-content-end">
                              <a class="btn btn-link mr-0" @click="deleteQuestion(questionForEdit)">
                                <i class="fas fa-trash" v-if="allCurrentQuestions.length > 1"></i>
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="mt-2">
                        <!--Question-->
                        <div class="px-3">
                          <div class="row mb-3">
                            <div class="col">
                              <ValidationProvider name="Question Text" rules="required" v-slot="validation">
                                <b-form-input class="fluid" size="lg" placeholder="New Step" v-model="questionForEdit.text"
                                  :state="spr2_getValidationState(validation)"
                                  aria-describedby="question-text-feedback" />
                                <b-form-invalid-feedback id="question-text-feedback">{{ validation.errors[0]
                                  }}</b-form-invalid-feedback>
                              </ValidationProvider>
                            </div>
                          </div>
                        </div>
                        <!--Flow validation alerts-->
                        <div class="px-3" v-if="stepIssuesFor(questionForEdit.id).length">
                          <b-alert :show="true" v-for="issue in stepIssuesFor(questionForEdit.id)"
                            :key="issue.key + '-' + issue.code" :variant="issue.severity === 'error' ? 'danger' : 'warning'"
                            role="alert" :aria-live="issue.severity === 'error' ? 'assertive' : 'polite'"
                            class="py-2 px-3 mb-2 small">
                            <i class="fas" :class="issue.severity === 'error' ? 'fa-ban' : 'fa-exclamation-triangle'"></i>
                            {{ issue.message }}
                          </b-alert>
                        </div>
                        <!--Yes/No-->
                        <div v-if="spr2_lookupTypeId(questionForEdit.typeId) === 'YesOrNo'">
                          <b-form inline class="form-question border-left border-success py-2 pr-3 pl-md-0 pl-3">
                            <label class="px-md-3 py-md-0 px-0 py-2 m-0">
                              <strong class="text-success">If Yes</strong>
                            </label>
                            <div
                              class="border-0 d-flex flex-md-row flex-column flex-grow-1 align-items-center h-100">
                              <b-form-select class="no-wrap ml-md-0 form-control flex-grow-1"
                                :class="branchSelectClass(questionForEdit.id, 'yes')"
                                :disabled="branchIsEnd(questionForEdit, 'yesResult')"
                                :options="destinationOptionsExcluding(questionForEdit.id)"
                                :value="branchValue(questionForEdit, 'yesResult')"
                                @change="onBranchChange(questionForEdit, 'yesResult', $event)">
                                <template #first>
                                  <b-form-select-option :value="null">— Select destination —</b-form-select-option>
                                </template>
                              </b-form-select>
                              <b-button size="sm" class="flow-end-chip ml-2"
                                :variant="branchIsEnd(questionForEdit, 'yesResult') ? 'danger' : 'outline-danger'"
                                @click="toggleBranchEnd(questionForEdit, 'yesResult')">
                                <i class="fas fa-ban"></i> END
                              </b-button>
                            </div>
                          </b-form>
                          <b-form inline class="form-question border-left border-danger py-2 pr-3 pl-md-0 pl-3">
                            <label class="px-md-3 py-md-0 px-0 py-2 m-0">
                              <strong class="text-danger">If No</strong>
                            </label>
                            <div
                              class="d-flex flex-row flex-grow-1 align-items-center h-100">
                              <b-form-select class="no-wrap ml-md-0 form-control flex-grow-1"
                                :class="branchSelectClass(questionForEdit.id, 'no')"
                                :disabled="branchIsEnd(questionForEdit, 'noResult')"
                                :options="destinationOptionsExcluding(questionForEdit.id)"
                                :value="branchValue(questionForEdit, 'noResult')"
                                @change="onBranchChange(questionForEdit, 'noResult', $event)">
                                <template #first>
                                  <b-form-select-option :value="null">— Select destination —</b-form-select-option>
                                </template>
                              </b-form-select>
                              <b-button size="sm" class="flow-end-chip ml-2"
                                :variant="branchIsEnd(questionForEdit, 'noResult') ? 'danger' : 'outline-danger'"
                                @click="toggleBranchEnd(questionForEdit, 'noResult')">
                                <i class="fas fa-ban"></i> END
                              </b-button>
                            </div>
                          </b-form>
                        </div>
                        <!--Text/Acknowledge-->
                        <div
                          v-if="spr2_lookupTypeId(questionForEdit.typeId) === 'TextInput' || spr2_lookupTypeId(questionForEdit.typeId) === 'Acknowledgement'">
                          <stepFlowFooter :step="questionForEdit" :issues="flowIssues"
                            :destination-options="destinationOptionsExcluding(questionForEdit.id)"
                            @change="onFlowFooterChange(questionForEdit, $event)" />
                        </div>
                        <!--Checklist-->
                        <div
                          v-if="spr2_lookupTypeId(questionForEdit.typeId) === 'Checklist' && questionForEdit.checklistItems"
                          class="pb-0 md-md-0 checklist">
                          <div class="px-3">
                            <label class="mb-0"><small><strong>Checklist items</strong></small></label>
                            <b-input-group v-for="(checklistItem, index) in questionForEdit.checklistItems"
                              v-bind:key="checklistItem.sortKey">
                              <b-input-group-prepend>
                                <b-input-group-text>{{index + 1}}</b-input-group-text>
                              </b-input-group-prepend>
                              <b-form-input v-model="checklistItem.text"></b-form-input>
                              <b-input-group-append>
                                <b-button variant="outline-secondary"
                                  @click="deleteCheckListItem(questionForEdit, index)">
                                  <i class="fas fa-minus-circle"></i>
                                </b-button>
                                <b-button variant="light" @click="addCheckListItem(questionForEdit, index)">
                                  <i class="fas fa-plus-circle text-success"></i>
                                </b-button>
                              </b-input-group-append>
                            </b-input-group>
                          </div>
                          <stepFlowFooter :step="questionForEdit" :issues="flowIssues"
                            :destination-options="destinationOptionsExcluding(questionForEdit.id)"
                            @change="onFlowFooterChange(questionForEdit, $event)" />
                        </div>
                        <!--Image Upload-->
                        <div v-if="spr2_lookupTypeId(questionForEdit.typeId) === 'ImageUpload'">
                          <div class="text-muted px-3">User will be prompted to upload images.</div>
                          <stepFlowFooter :step="questionForEdit" :issues="flowIssues"
                            :destination-options="destinationOptionsExcluding(questionForEdit.id)"
                            @change="onFlowFooterChange(questionForEdit, $event)" />
                        </div>
                      </div>
                    </div>
                    <!--Delete-->
                  </div>
                  <stepConnector :state="connectorInfo(questionForEdit).state"
                    :label="connectorInfo(questionForEdit).label" />
                  <div class="card-add my-1 rounded" @click="addNewQuestion(index+1)"><i class="fas fa-plus"></i></div>
                </div>
              </div>
            </div>
          </div>
          </div>
          <div class="col-md-4 col-12 procedure-sidebar-col">
            <validationSidebar :issues="sidebarIssues" :change-history="changeHistory" @jump="jumpToStep" @undo="undoLastChange" />
          </div>
          </div>
          <div class="container position-relative d-none">
            <div class="row">
              <div class="col d-flex justify-content-center py-1 mt-n2">
                <b-button v-if="mode==='details'" class="btn btn-lg btn-success" @click="addNewQuestion">
                  <i class="fas fa-plus"></i>
                  <span class="d-md-inline d-none">New</span> Step
                </b-button>
              </div>
            </div>
          </div>
        </b-form>
      </div>
    </ValidationObserver>
    <b-modal v-model="copyProcedureDetailsOpen" no-close-on-backdrop no-close-on-esc hide-header-close>
      <template v-slot:modal-title>
        <div class="d-flex align-items-center">Copy Procedure from {{procedureForCopy.sourceTypeName}}
          {{(procedureForCopy.sourceCategoryName ? `/${procedureForCopy.sourceCategoryName}` : '')}}</div>
      </template>
      <b-form>
        <b-alert :show="isDuplicateFlag && !isSameFlag" variant="warning">A procedure with this type and category
          already exists. Saving your changes will overwrite the existing procedure.</b-alert>
        <b-alert :show="isSameFlag" variant="warning">You cannot copy an existing procedure to itself</b-alert>
        <b-form-group label="Select the new Type and Category (optional) for your new procedure."></b-form-group>
        <b-form-group label="Response Type">
          <b-select :options="eventTypeOptions" v-model="selectedCopyType">
            <template #first>
              <b-form-select-option :value="null" disabled>(Required) The type for this
                procedure.</b-form-select-option>
            </template>
          </b-select>
        </b-form-group>
        <b-form-group label="Category">
          <b-select :disabled="selectedCopyType == null" :options="copyEventCategoryOptions"
            v-model="selectedCopyCategory">
            <template #first>
              <b-form-select-option :value="null" disabled>(Optional) Select this if you want the procedure to be
                specific to a category.</b-form-select-option>
            </template>
          </b-select>
        </b-form-group>
      </b-form>
      <template v-slot:modal-footer>
        <div>
          <b-button variant="light" @click="toggleCopyProcedureModal">Cancel</b-button>
          <b-button @click="doDuplicateProcedure()" :disabled="!procedureForCopy.eventType.id || isSameFlag"
            :variant="isDuplicateFlag ? 'warning': 'primary'">{{isDuplicateFlag?'Overwrite':'Copy'}}</b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import ProcedureNavigator from '../components/ProcedureNavigator'
import StepFlowFooter from '../components/StepFlowFooter'
import StepConnector from '../components/StepConnector'
import ValidationSidebar from '../components/ValidationSidebar'
import { snapshotSteps, describeStepChange } from '../utils/procedureHistory'
import { spr2Validation, spr2Procedures } from '../mixins/Spr2SharedFunctions'
import { buildFlowSteps, detectFlowIssues, hasBlockingIssues, ensureFlowFields, getStepState, describeConnector, describeYesNoConnector, mapServerIssuesToSidebarIssues, mirrorFlowToLegacy } from '../utils/procedureFlow'
export default {
  name: 'Properties',
  mixins: [spr2Validation, spr2Procedures],
  async created () {
    this.procedureFields = [
      { key: 'type', label: 'Type', sortable: true },
      { key: 'category', label: 'Category', sortable: true },
      { key: 'visibleTo', label: 'Visible To', sortable: true },
      { key: 'id', label: 'Actions' }]
    this.taskFields = [
      { key: 'name', label: 'Name', sortable: true },
      { key: 'id', label: 'Actions' }]
    let cv = parseInt(this.$route.params.cv)
    this.showView(cv)

    this.$watch(() => this.$route.params, (toParams, previousParams) => {
      let cv = parseInt(toParams.cv)
      this.showView(cv)
    })

    this.getProcedures()
    this.loadEventTypes()
    this.doLoadPositionTypes()
  },
  components: {
    procedureNavigator: ProcedureNavigator,
    stepFlowFooter: StepFlowFooter,
    stepConnector: StepConnector,
    validationSidebar: ValidationSidebar
  },
  data () {
    return {
      procedures: [],
      routineTaskProcedures: [],
      procedureForEdit: {
        eventType: { id: null },
        eventCategory: { id: null },
        sourceEventId: null,
        sourceTypeName: null,
        sourceCategoryId: null,
        sourceCategoryName: null
      },
      DetailsOpen: false,
      mode: 'details',
      procedureNavigatorIndex: 0,
      copyProcedureDetailsOpen: false,
      procedureForCopy: {
        eventType: { id: null },
        eventCategory: { id: null },
        sourceEventId: null,
        sourceTypeName: null,
        sourceCategoryId: null,
        sourceCategoryName: null
      },
      isDuplicateFlag: false,
      currentView: 1,
      allPositionTypesList: [],
      selectedPositionTypes: [],
      visibleToRolesExpanded: false,
      newQuestions: [],
      changeHistory: [],
      lastStepSnapshot: null,
      historyTimer: null,
      isRestoringHistory: false,
      pulseStepId: null,
      serverIssues: [],
      procedureFields: [],
      taskFields: [],
      isBusy: false,
      sortBy: 'name',
      sortDirection: 'asc',
      filter: null,
      sortDesc: true
    }
  },
  watch: {
    DetailsOpen: function (newVal, oldVal) {
      if (newVal === true) {
        this.mode = 'details'
      } else {
        this.clearChangeHistory()
      }
    },
    'procedureForEdit.questions': {
      deep: true,
      handler: function () {
        if (this.isRestoringHistory || this.lastStepSnapshot === null) {
          return
        }
        var self = this
        if (this.historyTimer) {
          clearTimeout(this.historyTimer)
        }
        this.historyTimer = setTimeout(function () {
          self.captureStepChange()
        }, 700)
      }
    },
    flowSteps: {
      deep: true,
      handler: function () {
        if (this.serverIssues.length) {
          this.serverIssues = []
        }
      }
    }
  },

  computed: {
    pageName () {
      return this.currentView === 1 ? 'Incidents' : 'Tasks / Inspections'
    },
    areaName () {
      return this.currentView === 1 ? 'Procedure' : 'Task / Inspection'
    },
    procedureTableData () {
      var returnProcedures = {
        fields: this.procedureFields
      }
      console.log(
        'Procedure Table Data is: ' +
        JSON.stringify(returnProcedures, null, 2)
      )
      return returnProcedures
    },
    taskTableData () {
      var returnTasks = {
        fields: this.taskFields
      }
      console.log(
        'Task Table Data is: ' +
        JSON.stringify(returnTasks, null, 2)
      )
      return returnTasks
    },
    isBlueRidgeOrMadisonCommunities () {
      return (this.$store.state.userCurrentCompany.companyName === 'Blue Ridge Companies' || this.$store.state.userCurrentCompany.companyName === 'Madison Communities')
    },
    isNewFlag () {
      return this.procedureForEdit === null || this.procedureForEdit.id === null
    },
    isSameFlag () {
      var pfc = this.procedureForCopy
      return pfc != null &&
      pfc.eventType != null &&
      pfc.eventCategory != null &&
      pfc.eventType.id === pfc.sourceEventId &&
      pfc.eventCategory.id === pfc.sourceCategoryId
    },
    allCurrentQuestions () {
      console.log(this.procedureForEdit)
      if (!this.procedureForEdit || !this.procedureForEdit.questions) {
        return []
      }
      return this.procedureForEdit.questions
    },
    selectedName: {
      get: function () {
        return this.procedureForEdit ? this.procedureForEdit.name : ''
      },
      set: function (val) {
        if (val !== null && this.procedureForEdit !== null) {
          this.procedureForEdit.name = val
        }
      }
    },
    selectedType: {
      get: function () {
        return this.procedureForEdit != null && this.procedureForEdit.eventType != null ? this.procedureForEdit.eventType.id : null
      },
      set: function (val) {
        if (val != null) {
          this.procedureForEdit.eventType.id = val
          this.procedureForEdit.eventType.name = this.$store.state.procedures.eventTypeOptions.find(
            (o) => o.id === this.procedureForEdit.eventType.id
          ).name
          this.doCheckForExistingDuplicates()
        }
      }
    },
    selectedCategory: {
      get: function () {
        if (this.procedureForEdit != null && this.procedureForEdit.eventCategory != null) {
          return this.procedureForEdit.eventCategory.id
        } else {
          return null
        }
      },
      set: function (val) {
        if (this.procedureForEdit == null) {
          return []
        }
        if (val == null) {
          this.procedureForEdit.eventCategory = null
        } else {
          var type = this.$store.state.procedures.eventTypeOptions.find(
            (o) => o.id === this.procedureForEdit.eventType.id
          )
          this.procedureForEdit.eventCategory = type.categories.find(
            (o) => o.id === val
          )
        }
        this.doCheckForExistingDuplicates()
      }
    },
    selectedCopyType: {
      get: function () {
        return this.procedureForCopy.eventType.id
      },
      set: function (val) {
        console.log('setting copy type: ' + val)
        this.procedureForCopy.eventType.id = val
        console.log('It is now: ' + this.procedureForCopy.eventType.id)
        this.procedureForCopy.eventType.name = this.$store.state.procedures.eventTypeOptions.find(
          (o) => o.id === this.procedureForCopy.eventType.id
        ).name
        if (!this.isSameFlag) {
          this.doCheckForExistingDuplicates()
        }
      }
    },
    selectedCopyCategory: {
      get: function () {
        if (this.procedureForCopy.eventCategory != null) {
          return this.procedureForCopy.eventCategory.id
        } else {
          return null
        }
      },
      set: function (val) {
        if (val == null) {
          this.procedureForCopy.eventCategory = null
        } else {
          var type = this.$store.state.procedures.eventTypeOptions.find(
            (o) => o.id === this.procedureForCopy.eventType.id
          )
          this.procedureForCopy.eventCategory = type.categories.find(
            (o) => o.id === val
          )
        }
        if (!this.isSameFlag) {
          this.doCheckForExistingDuplicates()
        }
      }
    },
    currentTypeName () {
      if (this.procedureForEdit != null && this.procedureForEdit.eventType != null) {
        return this.procedureForEdit.eventType.name
      } else {
        return ''
      }
    },
    currentCategoryName () {
      if (this.procedureForEdit != null && this.procedureForEdit.eventCategory != null) {
        return this.procedureForEdit.eventCategory.name
      } else {
        return ''/*  */
      }
    },
    eventCategoryOptions () {
      if (this.procedureForEdit == null || this.procedureForEdit.eventType == null) {
        return []
      }
      var type = this.$store.state.procedures.eventTypeOptions.find(
        (o) => o.id === this.procedureForEdit.eventType.id
      )
      // console.log('type: ' + JSON.stringify(type, null, 2))
      if (type === undefined) {
        return []
      }

      var results = type.categories.map((val) => {
        return { text: val.name, value: val.id }
      })
      return results
    },
    copyEventCategoryOptions () {
      var type = this.$store.state.procedures.eventTypeOptions.find(
        (o) => o.id === this.procedureForCopy.eventType.id
      )
      // console.log('type: ' + JSON.stringify(type, null, 2))
      if (type === undefined) {
        return []
      }
      var results = type.categories.map((val) => {
        return { text: val.name, value: val.id }
      })
      return results
    },
    allPositionTypesListOptions () {
      var activeTypes = this.allPositionTypesList.filter(item => item.active === true)
      var results = activeTypes.map((obj, index) => {
        return { text: obj.name, value: obj }
      })
      console.log(
        'Returning Position Types Options: ' + JSON.stringify(results, null, 2)
      )
      return results
    },
    eventTypeOptions () {
      if (this.procedureForEdit == null) {
        return []
      }
      var types = this.$store.state.procedures.eventTypeOptions
      var results = types.map((val) => {
        return { text: val.name, value: val.id }
      })
      return results
    },
    visibleToRolesCount () {
      var count = this.procedureForEdit && this.procedureForEdit.visibleToPositionTypes ? this.procedureForEdit.visibleToPositionTypes.length : 0
      return count
    },
    flowSteps () {
      var self = this
      return buildFlowSteps(this.allCurrentQuestions || [], function (typeId) {
        return self.spr2_lookupTypeId(typeId)
      })
    },
    flowIssues () {
      return detectFlowIssues(this.flowSteps || [])
    },
    hasBlockingFlowIssues () {
      return hasBlockingIssues(this.flowIssues)
    },
    flowStepsById () {
      var map = {}
      var steps = this.flowSteps || []
      steps.forEach(function (s) { map[s.id] = s })
      return map
    },
    questionLabelsById () {
      var map = {}
      var questions = this.allCurrentQuestions || []
      questions.forEach(function (q, idx) {
        map[q.id] = { label: 'Step ' + (idx + 1) }
      })
      return map
    },
    sidebarIssues () {
      var labels = this.questionLabelsById || {}
      var issues = this.flowIssues || []
      var mapped = issues.map(function (issue) {
        var label = labels[issue.stepId] ? labels[issue.stepId].label : 'Step'
        return Object.assign({}, issue, { stepLabel: label })
      })
      return mapped.concat(this.serverIssues || [])
    }
  },
  methods: {
    ...mapActions('procedures', [
      'getProcedures',
      'loadEventTypes',
      'saveProcedure',
      'getProcedureForEdit',
      'getProcedureForNew',
      'createQuestion',
      'duplicateProcedure',
      'checkForDuplicateProcedure',
      'getRoutineTaskProcedures'
    ]),
    ...mapActions('company', ['getPositionTypes']),
    procedureProvider (ctx) {
      this.isBusy = true
      console.log(
        'document type table ctx is: ' + JSON.stringify(ctx, null, 2)
      )
      var request = { sortBy: ctx.sortBy, sortDirection: ctx.sortDesc ? 'desc' : 'asc', search: ctx.filter }

      // Must return a promise that resolves to an array of items
      return this.getProcedures(request).then(r => {
        // Pluck the array of items off our axios response
        const items = r.data
        console.log(
          'Returned Procedure Table Data is: ' +
          JSON.stringify(items, null, 2)
        )
        this.isBusy = false
        this.procedures = items
        // Must return an array of items or an empty array if an error occurred
        return items || []
      })
    },
    taskProvider (ctx) {
      this.isBusy = true
      console.log(
        'document type table ctx is: ' + JSON.stringify(ctx, null, 2)
      )
      var request = { sortBy: ctx.sortBy, sortDirection: ctx.sortDesc ? 'desc' : 'asc', search: ctx.filter }

      // Must return a promise that resolves to an array of items
      return this.getRoutineTaskProcedures(request).then(r => {
        // Pluck the array of items off our axios response
        const items = r.data
        console.log(
          'Returned Routine Task Procedure Table Data is: ' +
          JSON.stringify(items, null, 2)
        )
        this.isBusy = false
        this.routineTaskProcedures = items
        // Must return an array of items or an empty array if an error occurred
        return items || []
      })
    },

    showView (viewIndex) {
      this.currentView = viewIndex
      this.DetailsOpen = false
    },
    toggleModal () {
      this.DetailsOpen = !this.DetailsOpen
    },
    toggleQuestionMode () {
      this.questionMode = !this.questionMode
    },
    showEditProcedureModal (id) {
      this.currentQuestionIndex = 0
      this.getProcedureForEdit(id).then(pfe => {
        this.procedureForEdit = pfe.data
        this.visibleToRolesExpanded = this.procedureForEdit && this.procedureForEdit.visibleToPositionTypes.length > 0
        this.initializeFlowFields(this.allCurrentQuestions)
        this.resetChangeHistory()
        this.toggleModal()
      })
    },
    showCopyProcedureModal (id, typeName, categoryName, sourceEventId, sourceCategoryId) {
      this.procedureForCopy.sourceProcedureId = id
      this.procedureForCopy.sourceEventId = sourceEventId
      this.procedureForCopy.sourceCategoryId = sourceCategoryId
      this.procedureForCopy.sourceCategoryName = categoryName
      this.procedureForCopy.sourceTypeName = typeName
      this.toggleCopyProcedureModal()
    },
    toggleCopyProcedureModal () {
      this.copyProcedureDetailsOpen = !this.copyProcedureDetailsOpen
      if (this.copyProcedureDetailsOpen) {
        this.procedureForCopy.eventType.id = null
        this.procedureForCopy.eventCategory.id = null
        this.isDuplicateFlag = false
      }
    },
    showNewProcedureModal (id) {
      this.isDuplicateFlag = false
      this.currentQuestionIndex = 0
      this.getProcedureForNew().then(pfn => {
        this.procedureForEdit = pfn.data.procedure
        this.initializeFlowFields(this.allCurrentQuestions)
        this.resetChangeHistory()
        this.toggleModal()
      })
    },
    getQuestionNumbersForSelection (id) {
      var selections = []
      var length = this.procedureForEdit.questions.length
      for (var i = 0; i < length; i++) {
        if (this.procedureForEdit.questions[i].id === id) continue
        selections.push({
          text: this.procedureForEdit.questions[i].text,
          value: this.procedureForEdit.questions[i].id
        })
      }
      return selections
    },
    doDuplicateProcedure () {
      const procedureForDupe = {
        sourceProcedureId: this.procedureForCopy.sourceProcedureId,
        newTypeId: this.procedureForCopy.eventType.id,
        newCategoryId: this.procedureForCopy.eventCategory
          ? this.procedureForCopy.eventCategory.id
          : null
      }
      this.duplicateProcedure(procedureForDupe).then(() => {
        this.$root.$emit('bv::refresh::table', 'procedures-table')
        this.toggleCopyProcedureModal()
        this.$toasted.show('Procedure Copied.', { duration: 4000 })
      })
    },
    async doCheckForExistingDuplicates () {
      var procedure = (this.isNewFlag) ? this.procedureForEdit : this.procedureForCopy
      const procedureForDupe = {
        sourceProcedureId: procedure.sourceProcedureId,
        newTypeId: procedure.eventType.id,
        newCategoryId: procedure.eventCategory
          ? procedure.eventCategory.id
          : null
      }
      console.log('Select Type: ', procedureForDupe.newTypeId, 'Selected Category: ', procedureForDupe.newCategoryId)
      this.checkForDuplicateProcedure(procedureForDupe).then((result) => {
        this.isDuplicateFlag = result
      })
    },
    async doSaveProcedure () {
      if (this.mode !== 'preview') { // hack to stop the preview buttons from submitting the form, fix this
        let view = this.currentView
        var procedureForSave = this.procedureForEdit
        var refresh = null

        if (view === 1) {
          refresh = () => { this.$root.$emit('bv::refresh::table', 'procedures-table') }
        } else {
          procedureForSave.flavor = 'RoutineTask'
          refresh = () => { this.$root.$emit('bv::refresh::table', 'tasks-table') }
        }
        var self = this
        mirrorFlowToLegacy(procedureForSave.questions, function (typeId) {
          return self.spr2_lookupTypeId(typeId)
        })
        this.saveProcedure(procedureForSave).then(() => {
          this.serverIssues = []
          this.toggleModal()
          refresh()
          this.$toasted.show('Procedure Saved.', { duration: 4000 })
        }).catch((error) => {
          if (error && Array.isArray(error.issues)) {
            this.serverIssues = mapServerIssuesToSidebarIssues(error.issues, this.allCurrentQuestions)
          }
        })
      }
    },
    addNewQuestion (index) {
      this.createQuestion(index).then((r) => {
        this.procedureForEdit.questions.splice(index, 0, r.data)
        let newQuestion = this.allCurrentQuestions[index]
        this.initializeFlowFields([newQuestion])
        this.newQuestions.push(newQuestion.id)
      })
    },
    deleteQuestion (deleteMe) {
      var deleteIndex = this.allCurrentQuestions.indexOf(deleteMe)
      let question = this.allCurrentQuestions[deleteIndex]
      let newQuestionIndex = this.newQuestions.indexOf(question.id)
      if (newQuestionIndex > -1) {
        this.newQuestions.splice(newQuestionIndex, 1)
      }
      console.log(`deleting question at index: ${deleteIndex}`)
      this.$delete(this.allCurrentQuestions, deleteIndex)
    },
    addCheckListItem (question, insertAfterIndex) {
      console.log(
        'Adding item to question: ' +
          JSON.stringify(question, null, 2) +
          'index: ' +
          insertAfterIndex
      )
      question.checklistItems.splice(insertAfterIndex + 1, 0, {
        text: '',
        sortKey: insertAfterIndex + 1
      })
      for (var i = 0; i < question.checklistItems.length; i++) {
        question.checklistItems[i].sortKey = i + 1
      }
      console.log(
        'New checklist ' + JSON.stringify(question.checklistItems, null, 2)
      )
    },
    deleteCheckListItem (question, deleteIndex) {
      this.$delete(question.checklistItems, deleteIndex)
      for (var i = 0; i < question.checklistItems.length; i++) {
        question.checklistItems[i].sortKey = i + 1
      }
      console.log(
        'New checklist ' + JSON.stringify(question.checklistItems, null, 2)
      )
    },
    checklistClicked (question) {
      console.log(
        'Checklist changed with question: ' + JSON.stringify(question, null, 2)
      )
      if (question.checklistItems.length === 0) {
        this.addCheckListItem(question, 0)
      }
      // console.log('Checklist changed with value' + value)
    },
    async doLoadPositionTypes () {
      return this.getPositionTypes().then(r => {
        this.allPositionTypesList = r.data.types
      })
    },
    formatPositionTypes (positionTypes) {
      if (typeof Array.isArray(positionTypes) && positionTypes.length) {
        return positionTypes.map(a => a.name.trim()).join(', ')
      } else {
        return 'All'
      }
    },
    toggleVisibleToExpanded () {
      this.visibleToRolesExpanded = !this.visibleToRolesExpanded
    },
    isNewQuestionFlag (id) {
      return this.newQuestions.includes(id)
    },
    resetChangeHistory () {
      var self = this
      this.clearChangeHistory()
      this.$nextTick(function () {
        if (self.historyTimer) {
          clearTimeout(self.historyTimer)
          self.historyTimer = null
        }
        self.changeHistory = []
        self.lastStepSnapshot = snapshotSteps(self.allCurrentQuestions)
      })
    },
    clearChangeHistory () {
      if (this.historyTimer) {
        clearTimeout(this.historyTimer)
      }
      this.historyTimer = null
      this.changeHistory = []
      this.lastStepSnapshot = null
    },
    captureStepChange () {
      if (this.historyTimer) {
        clearTimeout(this.historyTimer)
      }
      this.historyTimer = null
      if (this.isRestoringHistory || this.lastStepSnapshot === null) {
        return
      }
      var next = snapshotSteps(this.allCurrentQuestions)
      var label = describeStepChange(this.lastStepSnapshot, next)
      var history = this.changeHistory
      var last = history.length > 0 ? history[history.length - 1] : null
      var isBatched = !!label && label.indexOf('Changed Step') === 0 &&
        last !== null && last.label === label
      if (label && !isBatched) {
        this.changeHistory.push({ label: label, snapshot: this.lastStepSnapshot })
        if (this.changeHistory.length > 50) {
          this.changeHistory.splice(0, this.changeHistory.length - 50)
        }
      }
      this.lastStepSnapshot = next
    },
    undoLastChange () {
      if (this.historyTimer) {
        this.captureStepChange()
      }
      if (!this.changeHistory.length || !this.procedureForEdit) {
        return
      }
      var self = this
      var entry = this.changeHistory.pop()
      this.isRestoringHistory = true
      var restored = snapshotSteps(entry.snapshot)
      if (Array.isArray(this.procedureForEdit.questions)) {
        var len = this.procedureForEdit.questions.length
        this.procedureForEdit.questions.splice.apply(this.procedureForEdit.questions, [0, len].concat(restored))
      } else {
        this.$set(this.procedureForEdit, 'questions', restored)
      }
      this.initializeFlowFields(this.procedureForEdit.questions)
      var ids = this.procedureForEdit.questions.map(function (q) { return q.id })
      this.newQuestions = this.newQuestions.filter(function (id) { return ids.indexOf(id) > -1 })
      this.lastStepSnapshot = snapshotSteps(entry.snapshot)
      this.$nextTick(function () {
        if (self.historyTimer) {
          clearTimeout(self.historyTimer)
          self.historyTimer = null
        }
        self.isRestoringHistory = false
      })
    },
    initializeFlowFields (questions) {
      var self = this
      var list = questions || []
      list.forEach(function (question) {
        ensureFlowFields(question, function (obj, key, val) { self.$set(obj, key, val) })
      })
    },
    stepIssuesFor (stepId) {
      return this.flowIssues.filter(function (i) { return i.stepId === stepId })
    },
    stepCardStateClass (stepId) {
      var state = getStepState(this.flowIssues, stepId)
      if (state === 'error') {
        return 'border-danger step-state-error'
      }
      if (state === 'warning') {
        return 'border-warning step-state-warning'
      }
      return ''
    },
    truncateText (text, max) {
      var value = text || ''
      if (value.length <= max) {
        return value
      }
      return value.substring(0, max) + '…'
    },
    destinationOptionsExcluding (excludeId) {
      var self = this
      var opts = []
      this.allCurrentQuestions.forEach(function (q, idx) {
        if (q.id === excludeId) {
          return
        }
        opts.push({ text: 'Step ' + (idx + 1) + ' — ' + self.truncateText(q.text, 40), value: q.id })
      })
      // "END Procedure" is intentionally NOT included here. Ending a
      // procedure is done exclusively via the dedicated END button/chip,
      // not by selecting an option in a "Go to step" destination dropdown.
      return opts
    },
    branchValue (question, resultField) {
      var result = question[resultField]
      if (!result) {
        return null
      }
      if (result.type === 'goto') {
        return result.value != null ? result.value : null
      }
      return null
    },
    onBranchChange (question, resultField, val) {
      if (!val) {
        question[resultField] = null
      } else {
        question[resultField] = { type: 'goto', value: val }
      }
    },
    branchIsEnd (question, resultField) {
      var result = question[resultField]
      return !!(result && (result.type === 'NA' || result.type === 'end'))
    },
    toggleBranchEnd (question, resultField) {
      if (this.branchIsEnd(question, resultField)) {
        question[resultField] = null
      } else {
        question[resultField] = { type: 'NA', value: null }
      }
    },
    branchSelectClass (stepId, key) {
      var issues = this.flowIssues.filter(function (i) { return i.stepId === stepId && i.key === key })
      if (issues.some(function (i) { return i.severity === 'error' })) {
        return 'border-danger'
      }
      if (issues.some(function (i) { return i.severity === 'warning' })) {
        return 'border-warning'
      }
      return ''
    },
    onFlowFooterChange (question, change) {
      this.$set(question, 'flowAction', change.flowAction)
      this.$set(question, 'flowTarget', change.flowTarget)
    },
    connectorInfo (question) {
      var list = this.allCurrentQuestions || []
      var nextId = null
      for (var i = 0; i < list.length; i++) {
        if (list[i].id === question.id) {
          nextId = i + 1 < list.length ? list[i + 1].id : null
          break
        }
      }
      var flowStep = this.flowStepsById[question.id]
      var typeName = this.spr2_lookupTypeId(question.typeId)
      if (typeName === 'YesOrNo') {
        return describeYesNoConnector(flowStep, this.flowIssues, this.questionLabelsById, nextId)
      }
      if (!flowStep) {
        return { state: 'seq', label: '↓ Next Step' }
      }
      var info = describeConnector(flowStep, this.flowIssues, this.questionLabelsById)
      var link = flowStep.links && flowStep.links[0]
      if (info.state === 'linked' || (info.state === 'seq' && link && link.action === 'end')) {
        if (link && link.action === 'link' && nextId !== null && link.target === nextId) {
          return info
        }
        return { state: 'detached', label: info.label }
      }
      return info
    },
    jumpToStep (stepId) {
      var self = this
      var el = document.getElementById('step-card-' + stepId)
      if (!el) {
        return
      }
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      this.pulseStepId = stepId
      setTimeout(function () {
        if (self.pulseStepId === stepId) {
          self.pulseStepId = null
        }
      }, 1600)
    }
  }
}
</script>

<style>
.ui.selection.no-wrap.dropdown {
  white-space: nowrap;
  width: 400px;
}
.ui.selection.no-wrap.dropdown > .text {
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}
.ui.modals.active .modal {
  height: auto;
}
.procedure-modal {
  height: calc(100vh - 110px);
}
ol.procedure-items {
  counter-reset: li;
  padding: 0;
}
ol.procedure-items > li.card {
  border-radius: 1em;
}
ol.procedure-items > li.card:before {
  content: counter(li);
  counter-increment: li;
  position: absolute;
  background: rgba(0, 0, 0, 0.05);
  color: #888;
  padding: 0 15px;
  top: 0;
  left: 0;
  height: 100%;
  border-radius: 13px 0 0 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}
.procedure-items .card .btn-link{
  color: #ddd;
}
.procedure-items .card .btn-link:hover{
  color: #555;
}
.procedure-items .card-number {
  border-radius: 2em;
  width: 36px;
  min-width: 36px !important;
  height: 36px;
}

.procedure-items .card-question {
  position: relative;
}

.procedure-items .card-question:after {
  content:"";
  display: block;
  height: calc(100% - 40px);
  width: 2px;
  background-color: #ddd;
  border-radius: 2px;
  left: 16px;
  top: 40px;
  position: absolute;
}

.procedure-items .card-question:last-child:after {
  display: none;
}

@media screen and (max-width: 767px) {
  ol.procedure-items > li.card:before {
    border-radius: 13px 0 13px 0;
    height: 40px;
  }

  .procedure-items .card-question:after {
    display: none;
  }
}

.procedure-items:empty {
  background-color: rgba(0, 0, 0, 0.05);
  height: 180px;
  border-radius: 10px;
  position: relative;
}

.procedure-items:empty:after {
  content: 'Select "+New Step" to create your procedure.';
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  left: 0;
  top: 0px;
  color: #777;
  font-weight: bold;
}
.procedure-item:empty:before {
  content: "Procedures are comprised of a variety of steps. ";
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  left: 0;
  top: 0;
  color: #aaa;
  font-weight: 500;
}

/*Add Procedure Card*/
.procedure-items .card-add {
  display: flex;
  justify-content: center;
  visibility: hidden;
  position: relative;
  color: #ccc;
  cursor: pointer;
  border: 2px dashed #ddd;
  padding: 3px;
  margin-left: 60px;
  transition: all 0.5s ease-out allow-discrete;
  transition: 200ms linear 50ms;
}
.procedure-items:hover .card-add {
  visibility: visible;
}
.procedure-items .card-add i{
  font-size: 80%;
  line-height: 1.3;
}
.procedure-items .card-add:hover {
  color: #28a745;
  border-color: rgba(40, 167, 69,0.5);
  background: rgba(40, 167, 69,0.03);
}

/*Type Switch*/
.switch-btn {
  position: relative;
  padding: 0 !important;
  white-space: nowrap;
  transition: all ease-in-out .5s;
}
.switch-btn:hover {
  position: relative;
  padding: 0;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.15),
    inset 0 0 0 1px rgba(0, 0, 0, 0.1);
  background: rgba(255, 255, 255, 0.2);
  color: #222;
  transition: all ease-in-out .5s;
}
.switch-btn .custom-control {
  position: relative;
  margin: 0;
  padding: 0;
}
.switch-btn > span {
  z-index: 1;
}
.switch-btn .custom-control-label:after,
.switch-btn .custom-control-label:before {
  display: none;
}
.switch-btn .custom-control-label > span {
  position: relative;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  min-width: 30px;
  border: 1px solid rgba(0,0,0,0.1);
  border-radius: .5em;
}
.switch-btn .custom-control-label span strong{
  display: none;
}
.switch-btn:hover .custom-control-label span strong{
  display: inline-block;
  position: absolute;
  top: 100%;
  background: #fff;
  border-radius: 15px;
  padding: 3px 10px;
  right: 0;
  box-shadow: 0 3px 6px rgba(0,0,0,0.2);
}
.switch-btn .custom-control-input:checked ~ .custom-control-label {
  width: 100%;
}
.switch-btn .custom-control-input:checked ~ .custom-control-label span,
.switch-btn:hover .custom-control-input:checked ~ .custom-control-label span {
  color: #fff;
  box-shadow: none;
  border: none;
  padding: 0 8px;
}
.switch-btn:hover .custom-control-input:checked ~ .custom-control-label span strong,
.switch-btn .custom-control-input:checked ~ .custom-control-label span strong{
  display: inline-block;
  position: relative;
  padding: 0;
  background-color: transparent;
  box-shadow: none;
  top: 0;
  text-indent: 5px;
}
.switch-btn .custom-control-input:checked ~ .custom-control-label::before {
  position: absolute;
  display: block;
  width: 100%;
  height: 100%;
  border-radius: .5em;
  left: 0;
  top: 0;
  z-index: 0;
}
.switch-btn .custom-control-input:checked + span {
  color: #fff;
}
.checklist .form-control {
  border-radius: 4px;
}

/*Checklist*/
.checklist .input-group {
  margin-bottom: -1px;
}

.checklist .btn-light {
  background: transparent;
  border-color: transparent !important;
  width: 40px;
  height: 40px;
  border-radius: 50% !important;
  margin-left: 0 !important;
}

.checklist .btn-light:hover {
  background: rgba(0, 0, 0, 0.075);
  border-color: transparent !important;
}

.checklist .btn-outline-secondary {
  border-color: #ced4da;
  border-radius: 0 !important;
}

.checklist .input-group .input-group-prepend .input-group-text{
  border-radius: 0;
}

.checklist .input-group:first-of-type .input-group-prepend .input-group-text{
  border-radius: 8px 0 0 0;
}

.checklist .input-group:last-of-type .input-group-prepend .input-group-text{
  border-radius: 0 0 0 8px;
}

.checklist .input-group:only-of-type .input-group-prepend .input-group-text{
  border-radius: 8px 0 0 8px;
}

.checklist > .input-group:first-of-type .input-group-append .btn:nth-child(1){
  display: none;
}

.checklist > .input-group:last-of-type .input-group-append .btn:nth-child(1){
  border-radius: 0 0 8px 0 !important;
}

.checklist > .input-group:first-of-type .form-control{
  border-radius: 0 8px 0 0;
}

.checklist > .input-group:only-of-type .form-control{
  border-radius: 0 8px 8px 0;
}

.checklist .input-group .form-control{
  height: 40px;
}

.preview-questions .card{
  height: calc(100vh - 260px);
  overflow-y: auto;
  overflow-x: hidden;
}

.preview-questions .card-body {
  display: flex;
  align-items: center;
  justify-content: center;
}

/*Form Question*/
.form-question {
  display: flex;
  flex-direction: row !important;
  flex-wrap: nowrap;
}

.form-question.border-success {
  background-color: rgb(225, 249, 236);
}

.form-question.border-danger {
  background-color: rgb(249, 235, 235);
}

@media screen and (max-width: 767px) {
  .form-question {
    flex-direction: column !important;
    height: auto;
  }

  .form-question .card {
    width: 100%;
    flex-direction: column !important;
    border-radius: 0 0 .5em .5em !important;
  }

  .form-question .card > div{
    padding: 10px 0;
  }

  .form-question .card .form-control {
    border-radius: 0 0 .5em .5em !important;
    border-top: 1px solid #eee !important;
  }

  .form-question .custom-select {
    margin-top: 0.5em;
    width: 100% !important;
  }

  .procedure-items .card-add {
    visibility: visible;
    transition: none;
    margin-left: 45px;
  }
}

.form-question label {
  white-space: nowrap;
}

.form-question .card {
  box-shadow: 0 1px 2px rgba(0,0,0,0.15), 0 -1px 0 rgba(0,0,0,0.05);
  border-radius: 0 .5em .5em 0;
}

.form-question .card .form-control {
  border-radius: 0 .5em .5em 0;
  height: 36px;
  width: 100%;
  border: none;
  border-left: 1px solid rgba(0,0,0,0.1);
  background-color: #f9f9f9;
}
.truncate {
  min-width: 30px;
  max-width: 200px;
  display: inline-block;
  white-space: nowrap;      /*keep text on one line */
  overflow: hidden;         /*prevent text from being shown outside the border */
  text-overflow: ellipsis;  /*cut off text with an ellipsis*/
}
.newQuestion .card{
  animation-name: newQuestion;
  animation-duration: 1s;
  animation-timing-function: ease-out;
}
@keyframes newQuestion {
  0% {
    border-color: default;
    box-shadow: none;
    background-color: default;
    max-height: 0;
    opacity: 0;
  }
  30% {
    border-color: rgba(40, 167, 69,0.1);
    box-shadow: inset 0 0 0 4px rgba(40, 167, 69, 0.25);
    background-color: rgba(40, 167, 69, 0.1);
    opacity: 1;
    max-height: 230px;
  }
  60% {
    border-color: rgba(40, 167, 69,0.1);
    box-shadow: inset 0 0 0 4px rgba(40, 167, 69, 0.25);
    background-color: rgba(40, 167, 69, 0.1);
    opacity: 1;
    max-height: 230px;
  }
  100% {
    border-color: default;
    box-shadow: none;
    background-color: default;
    max-height: 230px;
    opacity: 1;
  }
}
.procedure-edit-scroll {
  height: calc(100vh - 50px);
  overflow-y: auto;
  overflow-x: hidden;
}
.procedure-edit-header {
  position: sticky;
  top: 0;
  z-index: 30;
  background-color: #fff;
}
.procedure-flow-row {
  margin-left: 0;
  margin-right: 0;
}
.procedure-main-col {
  min-width: 0;
  padding-left: 0;
  padding-right: 0;
}
.procedure-sidebar-col {
  min-width: 280px;
  max-width: 340px;
  padding-top: 1rem;
}
.procedure-sidebar-col .validation-sidebar {
  top: calc(1rem + 60px) !important;
  max-height: calc(100vh - 60px - 160px) !important;
}
@media screen and (max-width: 991px) {
  .procedure-sidebar-col {
    max-width: 100%;
  }
  .procedure-sidebar-col .validation-sidebar {
    top: 0.5rem !important;
    max-height: none !important;
  }
  .procedure-flow-row {
    flex-wrap: wrap !important;
  }
}
.step-pulse {
  animation: step-pulse-highlight 1.6s ease-out;
}
@keyframes step-pulse-highlight {
  0% {
    box-shadow: 0 0 0 4px rgba(0, 123, 255, 0.5);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(0, 123, 255, 0);
  }
}
.step-state-error {
  border-left: 3px solid #dc3545 !important;
}
.step-state-warning {
  border-left: 3px solid #ffc107 !important;
}
.category-chip {
  display: inline-block;
  border-radius: 0.5em;
  padding: .1em .6em;
  font-size: .75em;
  font-weight: 600;
  background-color: #f5f5f5;
  color: #666666;
}
.issue-count-chip {
  font-size: .8em;
}
.category-chip-success {
  background-color: #d4edda;
  color: #187f2c;
}
.category-chip-warning {
  background-color: #fdf3e0;
  color: #8c5429;
}
.category-chip-danger {
  background-color: #fae1e3;
  color: #89151c;
}
</style>
